import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Home, ArrowLeft } from 'lucide-react';
import Button from '../components/ui/Button';

const PageContainer = styled.div`
  min-height: calc(100vh - 160px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing[8]};
`;

const Content = styled.div`
  max-width: 600px;
  text-align: center;
`;

const ErrorCode = styled(motion.h1)`
  font-size: ${props => props.theme.typography.fontSize['6xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary[500]};
  margin: 0;
  line-height: 1;
`;

const Title = styled(motion.h2)`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  color: ${props => props.theme.colors.ink};
  margin: ${props => props.theme.spacing[6]} 0 ${props => props.theme.spacing[4]} 0;
`;

const Description = styled(motion.p)`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.textMuted};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  margin-bottom: ${props => props.theme.spacing[10]};
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.spacing[4]};
  justify-content: center;
  flex-wrap: wrap;
`;

const NotFound: React.FC = () => {
  return (
    <PageContainer>
      <Content>
        <ErrorCode
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          404
        </ErrorCode>

        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Page Not Found
        </Title>

        <Description
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          The page you're looking for doesn't exist or has been moved.
          Let's get you back on track.
        </Description>

        <ButtonGroup
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Button
            as={Link}
            to="/"
            variant="primary"
            icon={<Home />}
          >
            Go home
          </Button>

          <Button
            onClick={() => window.history.back()}
            variant="secondary"
            icon={<ArrowLeft />}
          >
            Go back
          </Button>
        </ButtonGroup>
      </Content>
    </PageContainer>
  );
};

export default NotFound;
